import { CancelToken } from "axios";
import { map, mapKeys, snakeCase } from "lodash";
import { useQuery } from "react-query";

import {
  makeSimpleDeleteRequest,
  makeSimpleGetRequest,
  makeSimplePatchRequest,
  makeSimplePostRequest,
} from "lib/axios";
import { makeQueryString } from "lib/dynamicRest";
import { skipRetriesOn404, useQueriesTyped } from "lib/react-query";
import { ORDERBOOK_URL } from "services/constants";
import { CredentialSubmissionFormValues } from "services/credentialSubmission/types";

import {
  AssetAssetAdministrationDistribution,
  AssetKYWCredentialSubmission,
  AssetKYWRoyaltyAccount,
} from "./types";

const assetKYWRoyaltyAccountsIncludesQueryParams = makeQueryString({
  include: ["royalty_account.royalty_payor.permissions.*"],
});

export const useAssetKYWRoyaltyAccounts = () =>
  useQuery(
    ["assetKYWRoyaltyAccount"],
    async () =>
      await makeSimpleGetRequest<AssetKYWRoyaltyAccount[]>(
        `${ORDERBOOK_URL}/assets-kyw-royalty-accounts/?${assetKYWRoyaltyAccountsIncludesQueryParams}`,
      ),
    { staleTime: 500 },
  );

const getAssetKYWRoyaltyAccount = async (
  assetId: string,
  cancelToken?: CancelToken,
): Promise<AssetKYWRoyaltyAccount> => {
  return makeSimpleGetRequest<AssetKYWRoyaltyAccount>(
    `${ORDERBOOK_URL}/assets-kyw-royalty-accounts/${assetId}/?${assetKYWRoyaltyAccountsIncludesQueryParams}`,
    {
      cancelToken,
    },
  );
};

export const useAssetKYWRoyaltyAccount = (assetId: string) =>
  useQuery(
    ["assetKYWRoyaltyAccount", assetId],
    async () => {
      return await exported.getAssetKYWRoyaltyAccount(assetId);
    },
    { enabled: !!assetId, retry: skipRetriesOn404 },
  );

export const createAssetKYWRoyaltyAccount = async (payload: {
  username: string;
  password: string;
  royalty_payor_id: string;
  subaccount_id: string | null;
  subaccount_name: string | null;
}): Promise<AssetKYWRoyaltyAccount> => {
  return await makeSimplePostRequest<AssetKYWRoyaltyAccount>(
    `${ORDERBOOK_URL}/assets-kyw-royalty-accounts/?${assetKYWRoyaltyAccountsIncludesQueryParams}`,
    JSON.stringify(payload),
    [200, 201],
  );
};

export const updateRoyaltyAccountPassword = async (
  assetId: number,
  password: string,
): Promise<AssetKYWRoyaltyAccount> => {
  return await makeSimplePatchRequest<AssetKYWRoyaltyAccount>(
    `${ORDERBOOK_URL}/assets-kyw-royalty-accounts/${assetId}/?${assetKYWRoyaltyAccountsIncludesQueryParams}`,
    JSON.stringify({ password }),
    [200],
  );
};

export const removeAssetKYWRoyaltyAccount = async (
  assetId: number,
): Promise<void> =>
  await makeSimpleDeleteRequest(
    `${ORDERBOOK_URL}/assets-kyw-royalty-accounts/${assetId}/`,
    [204],
  );

const assetKYWCredentialSubmissionsIncludesQueryParams = makeQueryString({
  include: ["credential_submission.royalty_payor.permissions.*"],
});

export const useAssetKYWCredentialSubmissions = () =>
  useQuery(
    ["assetKYWCredentialSubmission"],
    async () =>
      await makeSimpleGetRequest<AssetKYWCredentialSubmission[]>(
        `${ORDERBOOK_URL}/assets-kyw-credential-submissions/?${assetKYWCredentialSubmissionsIncludesQueryParams}`,
      ),
    { staleTime: 500 },
  );

export const useAssetKYWCredentialSubmission = (assetId: string) =>
  useQuery(
    ["assetKYWCredentialSubmission", assetId],
    async () => {
      return await makeSimpleGetRequest<AssetKYWCredentialSubmission>(
        `${ORDERBOOK_URL}/assets-kyw-credential-submissions/${assetId}/?${assetKYWCredentialSubmissionsIncludesQueryParams}`,
      );
    },
    { enabled: !!assetId, retry: skipRetriesOn404 },
  );

export const createAssetKYWCredentialSubmission = async (payload: {
  username: string;
  password: string;
  royalty_payor_id: string;
}): Promise<AssetKYWCredentialSubmission> => {
  return await makeSimplePostRequest<AssetKYWCredentialSubmission>(
    `${ORDERBOOK_URL}/assets-kyw-credential-submissions/?${assetKYWCredentialSubmissionsIncludesQueryParams}`,
    JSON.stringify(payload),
    [200, 201],
  );
};

export const updateCredentialSubmissionPassword = async (
  assetId: number,
  password: string,
): Promise<AssetKYWCredentialSubmission> => {
  return await makeSimplePatchRequest<AssetKYWCredentialSubmission>(
    `${ORDERBOOK_URL}/assets-kyw-credential-submissions/${assetId}/?${assetKYWCredentialSubmissionsIncludesQueryParams}`,
    JSON.stringify({ password }),
    [200],
  );
};

const mapPayloadKeys = (data: Record<string, any>) =>
  mapKeys(data, (_, key) => snakeCase(key));

export const updateCredentialSubmission = async (
  assetId: number,
  payload: CredentialSubmissionFormValues,
): Promise<AssetKYWCredentialSubmission> => {
  const formattedPayload = mapPayloadKeys(payload);
  return await makeSimplePatchRequest<AssetKYWCredentialSubmission>(
    `${ORDERBOOK_URL}/assets-kyw-credential-submissions/${assetId}/?${assetKYWCredentialSubmissionsIncludesQueryParams}`,
    JSON.stringify(formattedPayload),
    [200],
  );
};

export const removeAssetKYWCredentialSubmission = async (
  assetId: number,
): Promise<void> =>
  await makeSimpleDeleteRequest(
    `${ORDERBOOK_URL}/assets-kyw-credential-submissions/${assetId}`,
    [204],
  );

export const useManyAssetAssetAdministrationDistributions = (
  assetIds: number[],
) =>
  useQueriesTyped(
    map(assetIds, assetId => ({
      queryKey: ["assetAssetAdministrationDistributions", assetId],
      queryFn: () =>
        makeSimpleGetRequest<AssetAssetAdministrationDistribution[]>(
          `${ORDERBOOK_URL}/assets/${assetId}/asset-administration-distributions/?sort[]=-distribution.distribution_date&include[]=asset_administration.asset.*&include[]=active_asset_distribution_task.*&include[]=asset_administration.*&include[]=distribution.*`,
        ),
    })),
  );

const exported = {
  getAssetKYWRoyaltyAccount,
};
export default exported;
